import { filter, last, isEqual, intersection, includes } from 'lodash';
import moment from 'moment';

import { EVEN_SPLIT, SPECIFIED_PER_PERSON, FIXED_PER_PERSON } from './split-types';
import { PER_SLOT, TOTAL } from './cost-types';
import { SPREEDLY_CC_TYPES } from './card-type-names';

import Payment from './payment';

const isCanceled = status => status === 'canceled';

const isCreated = status => status === 'created';

const isActive = status => status === 'active';

const isCompleted = (purchase) => {
  if (purchase.status === 'completed') return true;
  const lastPayment = getLastPayment(purchase);
  return Payment.isPaid(lastPayment.status) || Payment.isCaptured(lastPayment.status);
};

const isWaitingForMerchantToComplete = (purchase) => {
  if (isCompleted(purchase)) return true;
  const payment = getCurrentOrLastPayment(purchase);
  return Payment.isAuthorized(payment.status);
};

const isEvenSplit = splitType => splitType === EVEN_SPLIT;

const isMultiPayment = payments => payments.length > 1;

const isPerSlot = costType => costType === PER_SLOT;

const isTotal = costType => costType === TOTAL;

const isSinglePayment = payments => payments.length === 1;

const isSpecifiedPerPerson = splitType => splitType === SPECIFIED_PER_PERSON;

const isFixedPerPerson = splitType => splitType === FIXED_PER_PERSON;

const isDateExpired = date => !!date && moment().isAfter(date);

const isCurrentPaymentLastPayment = (purchase) => {
  const { currentPayment } = purchase;

  return !!currentPayment && currentPayment.number === purchase.payments.length;
};

const isGroupFull = (purchase) => {
  if (!purchase.productOrGroupMaxSlots) return false;

  return purchase.committedSlots === purchase.productOrGroupMaxSlots;
};

const isPaymentExpired = payment => isDateExpired(payment.dueDeadline);

// DEPRECATED: Use Payment.isProcessing instead
const isPaymentProcessing = (payment) => {
  return Payment.isProcessing(payment.status);
};

const isLastPaymentBeingProcessed = (purchase) => {
  const lastPayment = getLastPayment(purchase);
  return Payment.isProcessing(lastPayment.status) || Payment.isPaid(lastPayment.status);
};

const getSuccessReturnUrlWithParams = (purchase) => {
  const { id, slug, successReturnUrl } = purchase;
  if (!successReturnUrl) return null;
  return `${successReturnUrl}?purchase_id=${id}&purchase_slug=${slug}`;
};

const getNextPayment = (purchase) => {
  const current = purchase.currentPayment;

  if (!current || current.number === 0) return null;

  if (isCurrentPaymentLastPayment(purchase)) return null;

  return purchase.payments[current.number];
};

const hasPaymentsAuthorizing = (purchase) => {
  const { payments } = purchase;
  return payments.reduce((res, p) => res && Payment.isAuthorizing(p.status), true);
};

const getLastPayment = (purchase) => {
  return last(purchase.payments);
};

const getCurrentOrLastPayment = (purchase) => {
  if (purchase.currentPayment) return purchase.currentPayment;
  return getLastPayment(purchase);
};

const getPaidPayments = (purchase) => {
  return filter(purchase.payments, payment => Payment.isPaid(payment.status));
};

const requirePurchaseConsumerFieldAnswers = (consumerFieldSchemas, consumerFieldValues) => {
  const requiredSchemaIds = consumerFieldSchemas.filter(field => field.required === true).map(f => f.id);
  const purchaseValueIds = consumerFieldValues.map(f => f.schemaId);

  if (!requiredSchemaIds.length) return false;
  return !isEqual(intersection(requiredSchemaIds, purchaseValueIds), requiredSchemaIds);
};

const getValidCardTypes = (currencyConfig) => {
  const { supportedPaymentSources } = currencyConfig;
  const unsupportedCards = [];
  const validCards = {};

  supportedPaymentSources.forEach((paymentSource) => {
    if (paymentSource.type === 'credit_card' && paymentSource.unsupportedCards.length > 0) {
      unsupportedCards.push(...paymentSource.unsupportedCards);
    }
  });

  Object.keys(SPREEDLY_CC_TYPES).filter((card) => {
    return !includes(unsupportedCards, card);
  }).forEach((card) => {
    validCards[card] = SPREEDLY_CC_TYPES[card];
  });

  return validCards;
}

const renderProductDescription = (description) => {
  return description.replace(/\\n/g, '<br />');
}

export default {
  isCanceled,
  isCreated,
  isActive,
  isCompleted,
  isWaitingForMerchantToComplete,
  isEvenSplit,
  isMultiPayment,
  isPerSlot,
  isTotal,
  isSinglePayment,
  isSpecifiedPerPerson,
  isFixedPerPerson,
  isDateExpired,
  isCurrentPaymentLastPayment,
  isGroupFull,
  isPaymentExpired,
  isPaymentProcessing,
  isLastPaymentBeingProcessed,
  requirePurchaseConsumerFieldAnswers,
  getSuccessReturnUrlWithParams,
  getLastPayment,
  getNextPayment,
  getCurrentOrLastPayment,
  getPaidPayments,
  getValidCardTypes,
  hasPaymentsAuthorizing,
  renderProductDescription,
};



// WEBPACK FOOTER //
// ./src/lib/purchase.js